import { useEffect, useRef, useCallback } from 'react';
import { trackEvent, trackPageView, trackUserEngagement, trackCustomEvent } from '../firebase';

// Hook para tracking automático de Analytics
export const useAnalytics = () => {
  const startTimeRef = useRef(Date.now());
  const lastActivityRef = useRef(Date.now());
  const engagementIntervalRef = useRef(null);
  const isActiveRef = useRef(true);

  // Función para trackear tiempo de permanencia
  const trackTimeSpent = useCallback((componentName) => {
    const timeSpent = Date.now() - startTimeRef.current;
    trackUserEngagement(timeSpent);
    trackCustomEvent('time_on_component', {
      component_name: componentName,
      time_spent_ms: timeSpent,
      time_spent_seconds: Math.round(timeSpent / 1000)
    });
  }, []);

  // Función para trackear eventos de interacción
  const trackInteraction = useCallback((action, element, additionalData = {}) => {
    trackEvent('user_interaction', {
      action: action,
      element: element,
      timestamp: Date.now(),
      ...additionalData
    });
    lastActivityRef.current = Date.now();
  }, []);

  // Función para trackear navegación/page views
  const trackNavigation = useCallback((pageName, pageTitle = '') => {
    trackPageView(pageName, pageTitle);
    startTimeRef.current = Date.now();
  }, []);

  // Función para trackear scroll
  const trackScroll = useCallback((scrollPercentage) => {
    if (scrollPercentage > 0 && scrollPercentage % 25 === 0) { // Track every 25%
      trackEvent('scroll_depth', {
        scroll_percentage: scrollPercentage,
        page_location: window.location.href
      });
    }
  }, []);

  // Setup automático de eventos del navegador
  useEffect(() => {
    let scrollTimeout;
    
    // Función para detectar scroll
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100);
        
        if (scrollPercentage >= 0) {
          trackScroll(scrollPercentage);
        }
      }, 150);
    };

    // Función para detectar clicks automáticamente
    const handleClick = (event) => {
      const element = event.target;
      const tagName = element.tagName.toLowerCase();
      const elementInfo = {
        tag: tagName,
        text: element.textContent?.substring(0, 50) || '',
        className: element.className || '',
        id: element.id || ''
      };

      // Trackear clicks en elementos importantes
      if (['button', 'a', 'input'].includes(tagName) || 
          element.getAttribute('role') === 'button' ||
          element.onclick) {
        trackInteraction('click', tagName, elementInfo);
      }
    };

    // Función para detectar cuando la página pierde/gana foco
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isActiveRef.current = false;
        const sessionTime = Date.now() - startTimeRef.current;
        trackCustomEvent('session_pause', {
          session_time_ms: sessionTime
        });
      } else {
        isActiveRef.current = true;
        startTimeRef.current = Date.now();
        trackCustomEvent('session_resume', {
          timestamp: Date.now()
        });
      }
    };

    // Función para trackear tiempo de inactividad
    const trackEngagement = () => {
      if (isActiveRef.current) {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        const totalTime = Date.now() - startTimeRef.current;
        
        // Si ha estado activo en los últimos 30 segundos
        if (timeSinceLastActivity < 30000) {
          trackUserEngagement(totalTime);
        }
      }
    };

    // Event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleClick, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Interval para trackear engagement cada 30 segundos
    engagementIntervalRef.current = setInterval(trackEngagement, 30000);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (engagementIntervalRef.current) {
        clearInterval(engagementIntervalRef.current);
      }
      
      clearTimeout(scrollTimeout);
    };
  }, [trackScroll, trackInteraction]);

  // Cleanup cuando el componente se desmonta
  useEffect(() => {
    return () => {
      const finalTime = Date.now() - startTimeRef.current;
      trackUserEngagement(finalTime);
    };
  }, []);

  return {
    trackInteraction,
    trackNavigation,
    trackTimeSpent,
    trackCustomEvent: trackCustomEvent
  };
};

// Hook simplificado para componentes individuales
export const useComponentAnalytics = (componentName) => {
  const startTimeRef = useRef(Date.now());
  const { trackTimeSpent, trackNavigation } = useAnalytics();

  useEffect(() => {
    // Track que el componente se montó
    trackNavigation(componentName);
    startTimeRef.current = Date.now();

    // Cleanup: track tiempo total cuando se desmonta
    return () => {
      trackTimeSpent(componentName);
    };
  }, [componentName, trackNavigation, trackTimeSpent]);

  return {
    trackInteraction: (action, element, data) => {
      trackCustomEvent('component_interaction', {
        component: componentName,
        action,
        element,
        ...data
      });
    }
  };
};